import { useCallback, useEffect, useState } from 'react'
import { useNavigate } from '@tanstack/react-router'
import { useQueryClient } from '@tanstack/react-query'
import { Lock, Delete, ArrowRight, LogOut } from 'lucide-react'
import { useUIStore } from '@/stores/uiStore'
import { useSessionStore } from '@/stores/sessionStore'
import { useClerkLogin } from '@/api/auth'

/**
 * Non-dismissable in-place PIN re-entry overlay.
 *
 * Raised by the API client's clerk-session recovery
 * (client.ts recoverDeadClerkSession) when the BE stops accepting the
 * clerk credential mid-shift (session_jti generation mismatch, expiry).
 * The register screen — cart, customer, query cache — stays alive
 * underneath; a successful PIN closes the overlay and refetches
 * everything that 403'd behind it, so recovery is seconds instead of a
 * full reload.
 *
 * Deliberately NOT dismissable: no X, no backdrop click, no Escape.
 * There is no working register to go back to — every register-scoped
 * call is being rejected until a fresh clerk token exists. The only
 * other exit is Logout, which drops the clerk (keeping the register
 * binding) and lands on the PIN page proper.
 */
export function ClerkPinReauthModal() {
  const reason = useUIStore((s) => s.clerkReauthRequired)
  const clearClerkReauth = useUIStore((s) => s.clearClerkReauth)
  const selectedRegister = useSessionStore((s) => s.selectedRegister)
  const clearClerkKeepRegister = useSessionStore((s) => s.clearClerkKeepRegister)
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const clerkLogin = useClerkLogin()

  const [pin, setPin] = useState('')
  const [error, setError] = useState<string | null>(null)
  const open = reason !== null

  const handleKeyPress = useCallback((key: string) => {
    setError(null)
    if (key === 'backspace') {
      setPin((prev) => prev.slice(0, -1))
    } else if (key === 'clear') {
      setPin('')
    } else if (pin.length < 12) {
      setPin((prev) => prev + key)
    }
  }, [pin.length])

  const handleSubmit = useCallback(async () => {
    if (pin.length < 3) {
      setError('PIN must be at least 3 digits')
      return
    }
    if (!selectedRegister) {
      // Register binding lost while the overlay was up (shouldn't happen
      // on the in-place path, which preserves it) — fall back to the
      // full re-entry flow rather than dead-ending here.
      clearClerkReauth()
      navigate({ to: '/select-register' })
      return
    }
    setError(null)
    try {
      // useClerkLogin stores the fresh clerk token + staff + register
      // binding on success — nothing else to persist here.
      await clerkLogin.mutateAsync({ pin, register_id: selectedRegister.id })
      setPin('')
      clearClerkReauth()
      // Everything that 403'd behind the overlay refetches with the
      // fresh session+clerk pair and the screen comes back to life.
      void queryClient.invalidateQueries()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Invalid PIN. Please try again.')
      setPin('')
    }
  }, [pin, selectedRegister, clerkLogin, clearClerkReauth, navigate, queryClient])

  // Physical keyboard support, active only while raised. Listens on
  // `window` in the capture phase, which fires BEFORE the barcode
  // scanner's `document`-capture listener — and stops propagation on
  // EVERY key so PIN digits (and the scanner's Enter terminator) can
  // never leak into scanner/shortcut handlers behind the overlay.
  // preventDefault only on keys we consume, so browser chrome
  // (devtools, refresh) still works.
  useEffect(() => {
    if (!open) return
    const onKeyDown = (e: KeyboardEvent) => {
      e.stopPropagation()
      // Also stop other window-capture listeners (ManagerApprovalDialog
      // underneath): stopPropagation doesn't reach same-target listeners.
      e.stopImmediatePropagation()
      if (e.key >= '0' && e.key <= '9') {
        e.preventDefault()
        handleKeyPress(e.key)
      } else if (e.key === 'Backspace') {
        e.preventDefault()
        handleKeyPress('backspace')
      } else if (e.key === 'Enter') {
        e.preventDefault()
        if (pin.length >= 3) void handleSubmit()
      }
      // Escape deliberately unhandled — the overlay is non-dismissable.
    }
    window.addEventListener('keydown', onKeyDown, { capture: true })
    return () => window.removeEventListener('keydown', onKeyDown, { capture: true })
  }, [open, handleKeyPress, handleSubmit, pin.length])

  const handleLogout = () => {
    clearClerkReauth()
    // Local-only teardown: the clerk credential is already dead
    // server-side (that's why we're here), so POST /clerk/logout would
    // just 403. Keep the register binding so /login renders the PIN
    // page directly instead of bouncing through /select-register.
    clearClerkKeepRegister()
    navigate({ to: '/login' })
  }

  if (!open) return null

  const numpadKeys = ['1', '2', '3', '4', '5', '6', '7', '8', '9', 'clear', '0', 'backspace']

  return (
    // No onClick on the backdrop — dismissal is PIN success or Logout only.
    <div className="fixed inset-0 z-9999 flex items-center justify-center bg-black/75 p-4 backdrop-blur-[5px]">
      <div className="w-full max-w-sm rounded-2xl bg-white p-6 shadow-2xl">
        <div className="mb-4 flex flex-col items-center text-center">
          <div className="mb-3 flex h-14 w-14 items-center justify-center rounded-full bg-amber-100">
            <Lock size={26} className="text-amber-600" />
          </div>
          <h2 className="text-lg font-bold text-gray-900">PIN Sign-In</h2>
          <p className="mt-1 text-sm text-gray-500">
            Session requires authentication to continue.
          </p>
        </div>

        {/* PIN dots */}
        <div className="mb-3 flex h-6 items-center justify-center gap-2">
          {pin.length === 0 ? (
            <span className="text-sm text-gray-300">Enter PIN</span>
          ) : (
            Array.from({ length: pin.length }).map((_, i) => (
              <span key={i} className="h-2.5 w-2.5 rounded-full bg-gray-800" />
            ))
          )}
        </div>

        {error && (
          <p className="mb-3 text-center text-sm text-red-600">{error}</p>
        )}

        {/* ph-no-capture: PostHog click-autocapture records button text
            ("Clicked button with text '4'"), which would make the PIN
            reconstructable from the replay event stream. */}
        <div className="mb-4 grid grid-cols-3 gap-2 ph-no-capture">
          {numpadKeys.map((key) => (
            <button
              key={key}
              type="button"
              onClick={() => handleKeyPress(key)}
              className="flex h-14 items-center justify-center rounded-xl bg-gray-100 text-lg font-semibold text-gray-800 transition-colors hover:bg-gray-200 active:bg-gray-300"
            >
              {key === 'backspace' ? <Delete size={20} /> : key === 'clear' ? 'C' : key}
            </button>
          ))}
        </div>

        <button
          type="button"
          onClick={() => void handleSubmit()}
          disabled={pin.length < 3 || clerkLogin.isPending}
          className="mb-3 flex w-full items-center justify-center gap-2 rounded-xl bg-green-600 py-3.5 font-semibold text-white transition-colors hover:bg-green-700 disabled:cursor-not-allowed disabled:bg-gray-300"
        >
          {clerkLogin.isPending ? 'Verifying…' : 'Continue'}
          {!clerkLogin.isPending && <ArrowRight size={18} />}
        </button>

        <button
          type="button"
          onClick={handleLogout}
          className="flex w-full items-center justify-center gap-2 py-2 text-sm font-medium text-gray-500 transition-colors hover:text-gray-700"
        >
          <LogOut size={16} />
          Logout
        </button>
      </div>
    </div>
  )
}
